import { fetchConditions } from '@home/services/common';
import { setLocaleWeekCfgOfMoment } from '@home/utils/date';
import { getGameInfoByIdPlus } from '@intelligence/services/common';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { getNullableMoment } from '@intelligence/v3/utils/date';

// 日期粒度
const granularities = ['daily', 'weekly', 'monthly'];
const momentGranularityMap: IRecord = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
};

export const buildSilenceExecutor = (task: Function, feedbackValue = {}) => {
  return (...args: unknown[]) => {
    try {
      return task(...args);
    } catch (error) {
      console.error(error);
      return feedbackValue;
    }
  };
};

const validateListOriginal = (key: string, params?: IRecord): IRecord => {
  const listStr = params?.[key];

  if (typeof listStr !== 'string') {
    return {};
  }

  const list = listStr
    .split(',')
    .map((itemStr) => itemStr.trim())
    .filter(isNotEmpty);
  const uniqueList = Array.from(new Set(list));

  if (isEmpty(uniqueList)) {
    return {};
  }

  return { [key]: uniqueList.join(',') };
};

const analysisCompanyOriginal = async (params?: IRecord): Promise<IRecord> => {
  if (isEmpty(params) || isEmpty(params.company)) {
    return {};
  }

  const companyIds: string[] = validateListOriginal('company', params).company?.split(',') || [];
  let companyNames: string[] = [];

  try {
    const res = await getGameInfoByIdPlus({
      entity_type: 'company',
      ids: companyIds,
      level: 'custom',
      custom_fields: ['name'],
    });
    const resData = res?.data || {};
    companyNames = companyIds
      .map((companyId) => {
        return resData?.[companyId]?.name || '';
      })
      .filter(isNotEmpty);
  } catch (error) {
    console.error(error);
    companyNames = [];
  }

  if (isEmpty(companyNames)) {
    return {};
  }

  return { company: companyNames.join(',') };
};

const analysisDateRangeOriginal = (granularity: string, startDateKey: string, endDateKey: string, params?: IRecord) => {
  if (isEmpty(params)) {
    return {};
  }

  if (!startDateKey || !endDateKey) {
    return {};
  }

  if (!granularities.includes(granularity)) {
    return {};
  }

  const momentGranularity = momentGranularityMap[granularity];
  const startDate = params[startDateKey] || '';
  const endDate = params[endDateKey] || '';
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);

  if (startDateM === null || endDateM === null) {
    return {};
  }

  if (startDateM.isAfter(endDateM)) {
    return {};
  }

  // 设置moment的周的第一天
  const revert = setLocaleWeekCfgOfMoment({ dow: 0, doy: 4 });
  const newStartDate = startDateM.startOf(momentGranularity).format('YYYY-MM-DD');
  const newEndDate = endDateM.endOf(momentGranularity).format('YYYY-MM-DD');
  // 恢复moment的周的第一天
  revert();

  return {
    [startDateKey]: newStartDate,
    [endDateKey]: newEndDate,
  };
};

const analysisGranularityDateRangeOriginal = (params?: IRecord) => {
  if (isEmpty(params)) {
    return {};
  }

  const { granularity } = params || {};

  if (!granularities.includes(granularity)) {
    return {};
  }

  const dateRange = analysisDateRangeOriginal(granularity, 'start_date', 'end_date', params);

  return {
    ...dateRange,
    granularity,
  };
};

const analysisCountryOriginal = async (params: IRecord): Promise<IRecord> => {
  if (isEmpty(params) || isEmpty(params.region)) {
    return {};
  }

  let validateCountries: string[] = [];

  try {
    const res: any = await fetchConditions({
      regionOptions: { target: 'region_list' },
    });
    const allCountries: any[] = res?.data?.regionOptions || [];
    const countries: string[] =
      validateListOriginal('region', params)
        ?.region?.split(',')
        ?.map((region: string) => region?.toLowerCase()) || [];

    validateCountries = countries?.filter((country) => {
      return allCountries?.find((item) => item?.country_name_abbre?.toLowerCase() === country);
    });
  } catch (error) {
    console.error(error);
    validateCountries = [];
  }

  if (isEmpty(validateCountries)) {
    return {};
  }

  return { region: validateCountries.join(',') };
};

interface IGenreResult {
  genre_source: string;
  genre_main_genres: string[];
  genre_sub_genres: string[];
  genre_level3_genres: string[];
  genre_level4_genres: string[];
}

type ISerializeGenreResult = {
  [T in keyof IGenreResult]: string;
};

// 品类数据源
const genreSources = ['iegg', 'sensortower', 'appmagic', 'annie'];
/**
 * 分析类别选择，将嵌套的类别树转换为扁平化的层级结构
 * @param params - 参数对象，包含 genre_source 和 genre_source_value
 * @returns 扁平化的类别结果，包含各级别的类别路径
 */
const analysisGenreOriginal = (params?: IRecord): Partial<ISerializeGenreResult> => {
  if (isEmpty(params)) {
    return {};
  }

  const genreSource = params?.genre_source || '';

  if (!genreSources.includes(genreSource)) {
    return {};
  }

  return {
    genre_source: genreSource,
    genre_main_genres: params?.genre_main_genres || '',
    genre_sub_genres: params?.genre_sub_genres || '',
    genre_level3_genres: params?.genre_level3_genres || '',
    genre_level4_genres: params?.genre_level4_genres || '',
  };
};

const analysisTags = (tagKeys: string[], params?: IRecord): IRecord<string> => {
  if (isEmpty(params)) {
    return {};
  }

  return tagKeys.reduce((acc, cur) => {
    const curData = validateListOriginal(cur, params);
    return { ...acc, ...curData };
  }, {});
};

const sensortowerTagKeys = ['art_style', 'camera_pov', 'product_model', 'setting', 'tag', 'theme', 'monetization'];
const analysisSensortowerTagsOriginal = (params?: IRecord): IRecord<string> => {
  return analysisTags(sensortowerTagKeys, params);
};

const appmagicTagKeys = [
  'appmagic_themes',
  'appmagic_features',
  'appmagic_ip',
  'appmagic_settings',
  'appmagic_art_styles',
];
const analysisAppmagicTagsOriginal = (params?: IRecord): IRecord<string> => {
  return analysisTags(appmagicTagKeys, params);
};

// Range 参数基础键
const rangeBaseKeys: string[] = [
  // 手游通用榜单页
  'au_range',
  'revenue_range',
  'arpu_range',
  'downloads_range',
  // pcConsole榜单页
  'pcu_range',
  'npd_physical_revenue_range',
  'mscience_digital_revenue_range',
  'steam_price_range',
  'steam_premium_revenue_range',
];
/**
 * 分析 Range 类型参数
 * 将 agent 侧提供的 range 参数转换为 URL 参数格式
 */
const analysisMinMaxRangeOriginal = (params?: IRecord): IRecord<string> => {
  if (isEmpty(params)) {
    return {};
  }

  const result: IRecord<string> = {};

  for (const rangeBaseKey of rangeBaseKeys) {
    const minRangeKey = `${rangeBaseKey}_min`;
    const maxRangeKey = `${rangeBaseKey}_max`;
    const minRange = params[minRangeKey];
    const maxRange = params[maxRangeKey];

    if (minRange !== undefined && minRange !== null) {
      result[minRangeKey] = minRange;
    }
    if (maxRange !== undefined && maxRange !== null) {
      result[maxRangeKey] = maxRange;
    }
  }

  return result;
};

/**
 * 分析 Growth 类型参数
 * 将 agent 侧提供的 {metric}_growth_compare_opt 和 {metric}_growth_compare_value
 * 转换为 URL 参数格式（保持 Agent 返回格式）
 *
 * @param growthPrefixes - Growth 类型筛选器的前缀数组，默认为 ['revenue']
 * @param params - agent 返回的 filters 参数
 * @returns URL 参数对象
 *
 * @example
 * // 输入: ['revenue'], { revenue_growth_compare_opt: '>=', revenue_growth_compare_value: 500000 }
 * // 输出: { revenue_growth_compare_opt: '>=', revenue_growth_compare_value: '500000' }
 */
const analysisGrowthOriginal = (growthPrefixes: string[] = ['revenue'], params?: IRecord): IRecord<string> => {
  if (isEmpty(params)) {
    return {};
  }

  const result: IRecord<string> = {};

  for (const prefix of growthPrefixes) {
    const optKey = `${prefix}_growth_compare_opt`;
    const valueKey = `${prefix}_growth_compare_value`;

    const opt = params[optKey];
    const value = params[valueKey];

    if (opt !== undefined && opt !== null) {
      result[optKey] = String(opt);
    }
    if (value !== undefined && value !== null) {
      result[valueKey] = String(value);
    }
  }

  return result;
};

const gsdPublisherSharedCodes = ['all', 'NETWORK', 'RETAIL'];
/**
 * 分析 GSD Publisher Shared 筛选器参数
 * @param params - agent 返回的 filters 参数
 * @returns URL 参数对象
 *
 * @example
 * // 输入: { gsd_publisher_shared: 'RETAIL' }
 * // 输出: { gsd_publisher_shared: 'RETAIL' }
 */
const analysisGSDPublisherSharedOriginal = (params?: IRecord): IRecord<string> => {
  if (isEmpty(params)) {
    return {};
  }

  const publisherValue = params?.gsd_publisher_shared || '';

  // 验证是否为有效值
  if (!gsdPublisherSharedCodes.includes(publisherValue)) {
    return {};
  }

  return { gsd_publisher_shared: publisherValue };
};

const relations = ['and', 'or'];
const analysisRelationalList = (relationKey: string, listKey: string, params?: IRecord): IRecord<string> => {
  if (isEmpty(params)) {
    return {};
  }

  const relation = params?.[relationKey];
  const list = validateListOriginal(listKey, params)?.[listKey] || '';

  if (!relations.includes(relation) || isEmpty(list)) {
    return {};
  }

  return {
    [relationKey]: relation,
    [listKey]: list,
  };
};

/**
 * 分析 Steam Tags 筛选器参数
 * 将 agent 侧提供的 steam_tag_relation 和 steam_tag 转换为 URL 参数格式
 *
 * @param params - agent 返回的 filters 参数
 * @returns URL 参数对象
 *
 * @example
 * // 输入: { steam_tag_relation: 'or', steam_tag: '1980s,2.5D,2D' }
 * // 输出: { steam_tags: '1980s,2.5D,2D', steam_tags_logic: 'or' }
 */
const analysisSteamTagsOriginal = (params?: IRecord): IRecord<string> => {
  return analysisRelationalList('steam_tag_relation', 'steam_tag', params);
};

/**
 * 分析 Game Engine 类型参数
 * 将 agent 侧提供的 game_engine_relation 和 game_engine
 * 转换为 URL 参数格式（保持 Agent 返回格式）
 *
 * @param params - agent 返回的 filters 参数
 * @returns URL 参数对象
 *
 * @example
 * // 输入: { game_engine_relation: 'or', game_engine: ['Abyss Engine', 'Adobe AIR'] }
 * // 输出: { game_engine_relation: 'or', game_engine: 'Abyss Engine,Adobe AIR' }
 */
const analysisGameEngineOriginal = (params?: IRecord): IRecord<string> => {
  return analysisRelationalList('game_engine_relation', 'game_engine', params);
};

/**
 * 分析 KPI 指标选择参数
 * 将 agent 侧提供的 kpi_metric_type 或 kpi_metric_dataSource 转换为 URL 参数格式
 * 优先使用 kpi_metric_type，如果不存在则使用 kpi_metric_dataSource
 *
 * @param params - agent 返回的 filters 参数
 * @returns URL 参数对象
 *
 * @example
 * // 输入: { kpi_metric_type: 'revenue' }
 * // 输出: { kpi_metric_type: 'revenue' }
 *
 * @example
 * // 输入: { kpi_metric_dataSource: 'mscience' }
 * // 输出: { kpi_metric_dataSource: 'mscience' }
 */
const analysisKpiMetricsOriginal = (params?: IRecord): IRecord<string> => {
  // 优先处理 kpi_metric_type
  const metricTypeValue = params?.kpi_metric_type;
  if (isNotEmpty(metricTypeValue)) {
    return validateListOriginal('kpi_metric_type', params);
  }

  // 如果 kpi_metric_type 不存在，则处理 kpi_metric_dataSource
  const metricDataSourceValue = params?.kpi_metric_dataSource;
  if (isNotEmpty(metricDataSourceValue)) {
    return validateListOriginal('kpi_metric_dataSource', params);
  }

  // 如果两个参数都不存在，返回空对象
  return {};
};

export const validateList = buildSilenceExecutor(validateListOriginal);
export const analysisCompany = buildSilenceExecutor(analysisCompanyOriginal);
export const analysisDateRange = buildSilenceExecutor(analysisDateRangeOriginal);
export const analysisGranularityDateRange = buildSilenceExecutor(analysisGranularityDateRangeOriginal);
export const analysisCountry = buildSilenceExecutor(analysisCountryOriginal);
export const analysisGenre = buildSilenceExecutor(analysisGenreOriginal);
export const analysisSensortowerTags = buildSilenceExecutor(analysisSensortowerTagsOriginal);
export const analysisAppmagicTags = buildSilenceExecutor(analysisAppmagicTagsOriginal);
export const analysisMinMaxRange = buildSilenceExecutor(analysisMinMaxRangeOriginal);
export const analysisGrowth = buildSilenceExecutor(analysisGrowthOriginal);
export const analysisGSDPublisherShared = buildSilenceExecutor(analysisGSDPublisherSharedOriginal);
export const analysisSteamTags = buildSilenceExecutor(analysisSteamTagsOriginal);
export const analysisGameEngine = buildSilenceExecutor(analysisGameEngineOriginal);
export const analysisKpiMetrics = buildSilenceExecutor(analysisKpiMetricsOriginal);
